<template>
  <div
    :class="{ 'crosshair-pointer': enableZoom }"
  >
    <highcharts
      ref="highcharts"
      :options="histogramOptions"
      :callback="chartLoaded"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { isEmpty } from 'lodash';

import { timeIntervalToValueMapping, findTimeInterval, HISTOGRAM_POINTS_LIMIT } from '@/data/getDateRanges';


const MAX_TICK_POSITIONS_TO_SHOW = 13;

function convertAggregatedDataToSeriesData(data, aggregatedBy, useEpoch, momentTimezoneId) {
  let series = [];
  let timeInterval = findTimeInterval(aggregatedBy);
  
  data.forEach(function processData(dataPoint) {
    let date = dataPoint[aggregatedBy];

    if (date) {
      if (!useEpoch) {
        if (momentTimezoneId) {
          date = moment.utc(date).tz(momentTimezoneId).format(timeIntervalToValueMapping[timeInterval].format);
        } else {
          date = moment.utc(date).format(timeIntervalToValueMapping[timeInterval].format);
        }
      }

      let data = {
        y: dataPoint.callCount,
        drilldown: timeIntervalToValueMapping[timeInterval].drilldown
      };

      if (useEpoch) {
        data.x = date;
      } else {
        data.name = date;
      }

      series.push(data);
    }
  });

  return series;
}

function checkIfUserSelectedIntervalCanBeZoomed({momentStartTime, momentEndTime}, timeGroupInterval) {
  return momentEndTime && momentEndTime.diff(momentStartTime, timeGroupInterval) > 1;
}

function checkIfMinuteTimeIntervalCanBeZoomed({ momentStartTime, momentEndTime }, timeGroupInterval, numberOfDataPoints) {
  return timeGroupInterval !== 'minute' 
          || (momentEndTime.diff(momentStartTime, 'seconds') >= 59 && numberOfDataPoints >= 5);
}

export default {
  name: 'HighchartsCallHistogram',

  props: {
    data: {
      type: Array,
      default() {
        return [];
      }
    },
    momentTimezoneId: {
      type: String,
      default: ''
    },
    groupTimeInterval: {
      type: String,
      default: 'auto'
    },
    computedTimeGroupInterval: {
      type: String,
      default: ''
    },
    showLoader: {
      type: Boolean,
      default: false
    },
    timeRange: {
      type: Array,
      default() {
        return [];
      }
    }
  },

  data() {
    return {
      chart: null,
      columnOptions: {},
    };
  },

  computed: {
    // Enable zoom if either data is not empty 
    // or if interval is not minute
    // or interval is minute level but it has more than 5 data points
    enableZoom() {
      return this.shouldAllowZoom({ momentStartTime: this.timeRange[0], momentEndTime: this.timeRange[1] });
    },

    readableTimeInterval() {
      return findTimeInterval(this.computedTimeGroupInterval);
    },

    useEpoch() {
      return this.computedTimeGroupInterval === timeIntervalToValueMapping['minute'].code;
    },

    showDragLabels() {
      return this.computedTimeGroupInterval === timeIntervalToValueMapping['minute'].code;
    },

    histogramOptions() {
      let series = [
        {
          id: 'Calls',
          name: 'Calls',
          type: 'column',
          data: convertAggregatedDataToSeriesData(this.data, this.computedTimeGroupInterval, this.useEpoch, this.momentTimezoneId),
          yAxis: 'call-count-axis',
          // pointPlacement: 0.09,
          turboThreshold: HISTOGRAM_POINTS_LIMIT,
          maxPointWidth: 80,
        }
      ];

      let useEpoch = this.useEpoch;

      return {
        series,
        drilldown: {
          drillUpButton: {
            theme: {
              display: 'none'
            }
          },
          activeAxisLabelStyle: {
            textDecoration: 'none',
            color: 'var(--reset-body-font-color) !important'
          },
        },
        title: false,
        loading: {
          style: {
            'backgroundColor': 'var(--reset-body-bg-color)',
            'opacity': 0.8
          }
        },
        chart: {
          zoomType: this.enableZoom ? 'x' : undefined,
          height: '10%',
          events: {
            selection: this.handleChartSelection,
            drilldown: this.onDrilldown,
          },
          showDragPoints: {
            enable: this.useEpoch,
            offsetInMinutes: moment.tz(this.momentTimezoneId).utcOffset()
          }
        },
      
        tooltip: {
          shared: true,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          borderColor: 'rgba(0, 0, 0, 0.9)',
          style: {
            color: '#fff'
          },
        },
        legend: {
          enabled: false
        },
        xAxis: [{
          crosshair: true,
          type: this.useEpoch ? 'datetime' : 'category',
          id: 'date-axis',
          labels: {
            style: {
              whiteSpace: 'nowrap',
              color: 'var(--reset-body-font-color) !important'
            },
          },
          dateTimeLabelFormats: {
            minute: '%l:%M %p'
          },
          tickPositioner() {
            if (!useEpoch && this.dataMin !== null && this.dataMax !== null) {
              let tick = this.dataMin;
              let totalCount = this.dataMax;
              let increment = Math.ceil(totalCount/MAX_TICK_POSITIONS_TO_SHOW);
              let positions = [];

              increment = increment === 0 ? 1 : increment;
              
              for (tick;tick - increment <= totalCount; tick += increment) {
                positions.push(tick);
              }

              return positions;
            }
          }
        }],
        yAxis: [{
          labels: {
            format: '{value}',
            style: {
              color: 'var(--reset-body-font-color) !important'
            },
          },
          title: undefined,
          id: 'call-count-axis'
        }],
        time: {
          timezoneOffset: this.useEpoch ? -moment.tz(this.momentTimezoneId).utcOffset() : null
        },
        credits: false,
        responsive: {
          rules: [{
            condition: {
              maxWidth: 1450
            },
            chartOptions: {
              chart: {
                height: '15%'
              },
            }
          },
          {
            condition: {
              maxWidth: 1080
            },
            chartOptions: {
              chart: {
                height: '25%'
              },
            }
          },
          {
            condition: {
              maxWidth: 800
            },
            chartOptions: {
              chart: {
                height: '30%'
              },
            }
          },
          {
            condition: {
              maxWidth: 640
            },
            chartOptions: {
              chart: {
                height: '45%'
              },
            }
          }]
        }
      };
    },

  },

  watch: {
    showLoader(newValue) {
      this.showChartLoader(newValue);
    },

    momentTimezoneId(newValue) {
      if (this.chart && newValue) {
        this.chart.update({
          time: {
            timezoneOffset: this.useEpoch ? -moment.tz(this.momentTimezoneId).utcOffset() : null
          },
          showDragPoints: {
            enable: this.useEpoch,
            offsetInMinutes: moment.tz(this.momentTimezoneId).utcOffset()
          }
        });
      }
    }
  },

  methods: {

    chartLoaded(chart) {
      this.chart = chart;
    },

    handleChartSelection(e) {
      if (this.useEpoch) {
        this.handleChartSelectionForEpochTime(e);
      } else {
        this.handleChartSelectionCategorizedTime(e);
      }

      return false;
    },

    handleChartSelectionForEpochTime(e) {
      let startTimeInEpoch = e.xAxis[0].min;
      let endTimeInEpoch = e.xAxis[0].max;

      let timeGroupInterval = this.readableTimeInterval;

      let momentStartTime = moment(startTimeInEpoch).startOf(timeGroupInterval);
      let momentEndTime = moment(endTimeInEpoch).endOf(timeGroupInterval);

      if (!this.shouldAllowZoom({ momentStartTime, momentEndTime })) {
        return false;
      }

      this.$emit('onRangeSelection', { 
        reportStartInEpoch: startTimeInEpoch, 
        reportEndInEpoch: endTimeInEpoch, 
        timeGroupInterval 
      });
    },

    handleChartSelectionCategorizedTime(e) {
      let startPosition = Math.round(e.xAxis[0].min);
      let endPosition = Math.round(e.xAxis[0].max);

      // Get epochs from chart data array, the above positions are the index of the element
      let startTimeInEpoch = this.data[startPosition][this.computedTimeGroupInterval];
      let endTimeInEpoch = this.data[endPosition][this.computedTimeGroupInterval];

      let timeGroupInterval = this.readableTimeInterval;

      let momentStartTime = moment(startTimeInEpoch).startOf(timeGroupInterval);
      let momentEndTime = moment(endTimeInEpoch).endOf(timeGroupInterval);

      if (!this.shouldAllowZoom({ momentStartTime, momentEndTime })) {
        return false;
      }

      this.$emit('onRangeSelection', { 
        reportStartInEpoch: startTimeInEpoch, 
        reportEndInEpoch: endTimeInEpoch, 
        timeGroupInterval 
      });
    },

    onDrilldown(e) {
      // Get epochs from chart data array
      let pointTimeInEpoch = this.data[e.point.category][this.computedTimeGroupInterval];

      let timeGroupInterval = this.readableTimeInterval;

      let momentStartTime = moment(pointTimeInEpoch).startOf(timeGroupInterval);
      let momentEndTime = moment(pointTimeInEpoch).endOf(timeGroupInterval);

      if (!this.shouldAllowZoom({ momentStartTime, momentEndTime })) {
        return false;
      }

      this.$emit('onRangeSelection', { 
        reportStartInEpoch: pointTimeInEpoch, 
        reportEndInEpoch: pointTimeInEpoch, 
        timeGroupInterval 
      });
    },

    shouldAllowZoom({ momentStartTime, momentEndTime }) {
      // 1. Check if data is empty
      // 2. Check if user is looking at minute grouped data, if yes, these conditions should be satisfied:
      //    - Check if time range is for more than a minute
      //    - Check if number of existing data points are more than 4, since it we don't have 'second' level data
      // 3. Check if user has selected a grouped time interval, if yes, then does the time range more than a unit of the grouping interval.
      //    Eg. If user has selected group by 'day', then the time range should be more than a day, else we would show the same data 
      return !isEmpty(this.data) 
              && checkIfMinuteTimeIntervalCanBeZoomed({ momentStartTime, momentEndTime }, this.readableTimeInterval, this.data.length)
              && (this.groupTimeInterval === 'auto' || checkIfUserSelectedIntervalCanBeZoomed({ momentStartTime, momentEndTime }, this.readableTimeInterval));
    },

    showChartLoader(show) {
      if (!this.chart) {
        return;
      }

      if (show) {
        this.chart.showLoading(`Loading data...`);
      } else {
        this.chart.hideLoading();
      }
    },

  },
};
</script>

<style lang="scss" scoped>
.call-logs__time-group-container {
  width: 130px;
  margin: 0 auto;
}
</style>